"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { clearSessionWorkflowState } from "@/lib/session-workflow-state";

export type CoreUser = {
  id: string;
  email: string;
  display_name: string;
  platform_operator: boolean;
  capabilities?: string[];
  timezone?: string;
  /** ADR 0016 — local account with a temporary password; only /trocar-senha is usable. */
  password_must_change?: boolean;
  /** ADR 0020 — local TOTP confirmed. */
  mfa_enrolled?: boolean;
  /** ADR 0020 — session restricted to /me and MFA enrollment. */
  mfa_enrollment_required?: boolean;
  /** Optional: "local" | "microsoft" when the Core exposes it on /me. */
  auth_method?: string;
  kind?: string;
  business_unit_id?: string | null;
};

export function mustChangePassword(user: CoreUser | null | undefined): boolean {
  return Boolean(user?.password_must_change);
}

export function mustEnrollMfa(user: CoreUser | null | undefined): boolean {
  return Boolean(user?.mfa_enrollment_required) && !mustChangePassword(user);
}

export function canManageMfaPolicy(user: CoreUser | null | undefined): boolean {
  return Boolean(user?.platform_operator);
}

/** Local (password) accounts can change their own password. */
export function canChangeOwnPassword(user: CoreUser | null | undefined): boolean {
  if (!user) return false;
  if (user.password_must_change) return true;
  if (user.auth_method) return user.auth_method === "local";
  return user.kind === "local";
}

export function hasCapability(user: CoreUser | null | undefined, action: string): boolean {
  return Boolean(user?.capabilities?.includes(action));
}

export function canViewAudit(user: CoreUser | null | undefined): boolean {
  return hasCapability(user, "org.audit.read");
}

export function canApproveIdentityLink(user: CoreUser | null | undefined): boolean {
  return Boolean(
    user && (user.platform_operator || hasCapability(user, "org.identity_link.approve")),
  );
}

export function canViewProfiles(user: CoreUser | null | undefined): boolean {
  return Boolean(
    user &&
    (user.platform_operator ||
      hasCapability(user, "org.profile.read") ||
      hasCapability(user, "org.profile.create")),
  );
}

export function canViewGroups(user: CoreUser | null | undefined): boolean {
  return Boolean(
    user &&
    (user.platform_operator ||
      hasCapability(user, "org.group.read") ||
      hasCapability(user, "org.group.create")),
  );
}

// Blocking/unblocking changes the identity in every tenant, so the Core
// requires org.user.update at platform scope (#148). /me capabilities carry
// no scope, so the portal offers it to platform operators only; the Core
// stays the authority.
export function canChangeUserStatus(user: CoreUser | null | undefined): boolean {
  return Boolean(user?.platform_operator);
}

export function canViewUsers(user: CoreUser | null | undefined): boolean {
  return Boolean(user && (user.platform_operator || hasCapability(user, "org.user.read")));
}

type CoreAuthValue = {
  user: CoreUser | null;
  loading: boolean;
  refresh: () => Promise<CoreUser | null>;
  clear: () => void;
};

const CoreAuthContext = createContext<CoreAuthValue | null>(null);

type Snapshot = { user: CoreUser | null; loading: boolean };

let snapshot: Snapshot = { user: null, loading: true };
const serverSnapshot: Snapshot = { user: null, loading: true };
const listeners = new Set<() => void>();
let bootstrapped = false;

function emit(next: Snapshot) {
  snapshot = next;
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return snapshot;
}

function getServerSnapshot(): Snapshot {
  return serverSnapshot;
}

async function fetchMe(): Promise<CoreUser | null> {
  try {
    const res = await fetch("/api/v1/me", { credentials: "include", cache: "no-store" });
    if (!res.ok) {
      return null;
    }
    const data = (await res.json()) as CoreUser;
    if (!data?.id || !data?.email) {
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function ensureBootstrap() {
  if (bootstrapped || typeof window === "undefined") {
    return;
  }
  bootstrapped = true;
  void fetchMe().then((user) => {
    emit({ user, loading: false });
  });
}

export function CoreAuthProvider({ children }: { children: ReactNode }) {
  const current = useSyncExternalStore(
    (listener) => {
      ensureBootstrap();
      return subscribe(listener);
    },
    getSnapshot,
    getServerSnapshot,
  );

  const refresh = useCallback(async () => {
    emit({ ...snapshot, loading: true });
    const user = await fetchMe();
    emit({ user, loading: false });
    return user;
  }, []);

  const clear = useCallback(() => {
    clearSessionWorkflowState();
    emit({ user: null, loading: false });
  }, []);

  const value = useMemo<CoreAuthValue>(
    () => ({ user: current.user, loading: current.loading, refresh, clear }),
    [current.user, current.loading, refresh, clear],
  );

  return <CoreAuthContext.Provider value={value}>{children}</CoreAuthContext.Provider>;
}

export function useCoreAuth(): CoreAuthValue {
  const ctx = useContext(CoreAuthContext);
  if (!ctx) {
    throw new Error("CoreAuthProvider ausente");
  }
  return ctx;
}
